import {
  CondicaoFiltroAvancadoV2,
  FiltroAvancadoV2FormValue,
  FiltroAvancadoV2Payload,
  GrupoCondicoesFiltroAvancadoV2,
  OperadorLogicoFiltroAvancadoV2,
  PeriodoFiltroAvancadoV2
} from '../models/filtro-avancado-v2.model';

export const DEFAULT_OPERADOR_LOGICO_AND_ID = 1;
export const DEFAULT_OPERADOR_LOGICO_OR_ID = 2;
export const MAX_GRUPOS_CONDICOES = 5;
// tipo_operador_id "Entre" (enum de operadores do manager)
export const OPERADOR_ENTRE_ID = 7;
export const CHAVE_CAMPO_CLIENTE = '$clients';
export const CHAVE_CAMPO_UNIDADE = '$units';
// Única entidade do filtro hoje (tb_filtro_sistema_entidade), já vem selecionada em período e condição.
export const ENTIDADE_TICKET_ID = 1;
// O filtro só tem a entidade Ticket: a lista vai pronta para o select abrir já preenchido (sem esperar a API).
export const ENTIDADES_FILTRO = [{ id: ENTIDADE_TICKET_ID, text: 'Ticket' }];
// Campos personalizados (MIL-30113): na tela cada campo tem a chave $tickets.personalizado-{id}; no manager a condição
// é gravada no campo contêiner com valor {"campo_personalizado_id": id, "valor": ...}.
export const CHAVE_CAMPOS_PERSONALIZADOS = '$$tickets.custom_fields';
export const PREFIXO_CHAVE_CAMPO_PERSONALIZADO = '$tickets.personalizado-';

// O operador_id salvo na condição é o id da linha em tb_filtro_sistema_operador, que muda conforme o
// tipo do campo; por isso os operadores são buscados por tipo_campo_id e exibidos pelo tipo_operador_id.
export const TEXTO_TIPO_OPERADOR = { 1: 'Igual', 2: 'Maior ou igual', 3: 'Diferente', 4: 'Menor ou igual', 5: 'Maior', 6: 'Menor', 7: 'Entre' };
export const TIPOS_OPERADOR_CONDICAO = [1, 3];
export const TIPOS_OPERADOR_PERIODO = [1, 5, 6, 4, 2, OPERADOR_ENTRE_ID];

// Dashboards: o filtro manual convive com o filtro avançado novo (MIL-30049). Do filtro manual saem a opção
// "Filtro avançado" antiga e o que ela gravava; a seleção do novo vai em filter_ids.
const CHAVES_FILTRO_AVANCADO_ANTIGO = ['personalizado', 'nome_filtro', 'filter_ids'];

export function semFiltroAvancadoAntigo(filtros: Array<any>): Array<any> {
  return (filtros || []).filter((filtro) => filtro.tipo !== 'filtro_avancado');
}

export function limpaFiltroManual(filtros: any): any {
  const limpo = { ...(filtros || {}) };
  Object.keys(limpo)
    .filter((chave) => CHAVES_FILTRO_AVANCADO_ANTIGO.includes(chave) || chave.startsWith('personalizado-'))
    .forEach((chave) => delete limpo[chave]);
  return limpo;
}

export function combinaFiltroManualComAvancado(filtrosManuais: any, filterIds: Array<any>): any {
  const filtros = limpaFiltroManual(filtrosManuais);
  if (filterIds?.length) {
    filtros.filter_ids = filterIds;
  }
  return filtros;
}

export function montaOperadores(operadoresApi: any[], tiposPermitidos: number[]): Array<{ id: number; text: string; tipo_operador_id: number }> {
  return tiposPermitidos
    .map((tipo) => {
      const operador = (operadoresApi || []).find((item) => Number(item.tipo_operador_id) === tipo);
      return operador ? { id: Number(operador.id), text: TEXTO_TIPO_OPERADOR[tipo], tipo_operador_id: tipo } : null;
    })
    .filter((operador) => !!operador);
}

export function isFiltroAvancadoV2(filtro: any): boolean {
  if (!filtro) {
    return false;
  }

  const periodos = filtro.periodos || filtro.periods || filtro.filterPeriods;
  const grupos = filtro.grupos || filtro.groups || filtro.filterGroups;
  const hasExplicitVersion = filtro.versao === 2 || filtro.version === 2 || filtro.tipo === 'avancado_v2' || filtro.is_avancado_v2 === true;

  return hasExplicitVersion || hasProperty(filtro, ['periodos', 'periods', 'filterPeriods']) || hasProperty(filtro, ['grupos', 'groups', 'filterGroups']) || hasItems(periodos) || hasItems(grupos);
}

export function shouldOpenFiltroV2(filtro?: any): boolean {
  return !filtro || isFiltroAvancadoV2(filtro);
}

// campos: lista do dropdown de campos, usada para o nome e o tipo dos campos personalizados.
export function fromApiToForm(filtro: any, tela: string, campos: any[] = []): FiltroAvancadoV2FormValue {
  const formValue: FiltroAvancadoV2FormValue = {
    id: filtro?.id,
    nome: filtro?.nome || '',
    tela: filtro?.tela || tela,
    visibilidade_id: filtro?.visibilidade_id || null,
    condicoes: condicoesFromApi(filtro?.condicoes || filtro?.filterCondicion || [], campos),
    grupos: gruposFromApi(filtro?.grupos || filtro?.groups || filtro?.filterGroups || [], campos),
    periodos: periodosFromApi(filtro?.periodos || filtro?.periods || filtro?.filterPeriods || [])
  };

  return formValue;
}

export function legadoToForm(filtro: any, tela: string): FiltroAvancadoV2FormValue {
  return {
    id: filtro?.id,
    nome: filtro?.nome || '',
    tela: filtro?.tela || tela,
    visibilidade_id: filtro?.visibilidade_id || null,
    condicoes: condicoesFromApi(filtro?.filterCondicion || filtro?.condicoes || []),
    grupos: [],
    periodos: []
  };
}

export function toPayload(formValue: FiltroAvancadoV2FormValue): FiltroAvancadoV2Payload {
  return {
    nome: (formValue.nome || '').trim(),
    tela: formValue.tela,
    visibilidade_id: Number(formValue.visibilidade_id),
    condicoes: stripCampoText(normalizeCondicoes(formValue.condicoes || [])),
    grupos: normalizeGrupos(formValue.grupos || []).map((grupo) => ({
      ...grupo,
      condicoes: stripCampoText(grupo.condicoes)
    })),
    periodos: stripCampoText(normalizePeriodos(formValue.periodos || []))
  };
}

function stripCampoText<T extends { campo_text?: string }>(items: T[]): T[] {
  return (items || []).map(({ campo_text, ...resto }) => resto as T);
}

export function hasEmptyGroups(grupos: GrupoCondicoesFiltroAvancadoV2[]): boolean {
  return (grupos || []).some((grupo) => !grupo.condicoes || grupo.condicoes.length === 0);
}

export function normalizeCondicoes(condicoes: any[]): CondicaoFiltroAvancadoV2[] {
  const normalizadas = (condicoes || []).map((condicao: any) => ({
    entidade_id: normalizeId(condicao.entidade_id || condicao.entidade?.id),
    campo_id: normalizeId(condicao.campo_id || condicao.campo?.id),
    campo_text: condicao.campo_text || condicao.campo?.text,
    operador_id: normalizeId(condicao.operador_id || condicao.operador?.id),
    valor: condicao.campo_personalizado_id
      ? JSON.stringify({ campo_personalizado_id: Number(condicao.campo_personalizado_id), valor: normalizeValor(condicao.valor) })
      : normalizeValor(condicao.valor),
    tipo_campo_id: normalizeId(condicao.tipo_campo_id || condicao.campo?.tipo_campo_id),
    campo_chave: condicao.campo_personalizado_id ? CHAVE_CAMPOS_PERSONALIZADOS : (condicao.campo_chave || condicao.campo?.chave)
  })).filter((condicao) => !!condicao.campo_id && !!condicao.operador_id);

  // Dentro de um grupo (ou da lista raiz), as condições são sempre ligadas por E/AND.
  return normalizadas.map((condicao, index) => {
    if (index === normalizadas.length - 1) {
      return condicao as CondicaoFiltroAvancadoV2;
    }

    return {
      ...condicao,
      operador_logico_id: DEFAULT_OPERADOR_LOGICO_AND_ID as OperadorLogicoFiltroAvancadoV2
    } as CondicaoFiltroAvancadoV2;
  });
}

export function condicoesFromApi(condicoes: any[], campos: any[] = []): CondicaoFiltroAvancadoV2[] {
  const normalizadas = (condicoes || []).map((condicao: any) => {
    const personalizado = campoPersonalizadoFromApi(condicao, campos);
    const tipoCampoId = personalizado ? personalizado.tipo_campo_id : normalizeId(condicao.tipo_campo_id || condicao.campo?.tipo_campo_id);
    const valor = personalizado ? personalizado.valor : condicao.valor;
    return {
      entidade_id: normalizeId(condicao.entidade_id || condicao.entidade?.id),
      campo_id: normalizeId(condicao.campo_id || condicao.campo?.id),
      campo_text: personalizado ? personalizado.campo_text : (condicao.campo_text || condicao.campo?.text),
      operador_id: normalizeId(condicao.operador_id || condicao.operador?.id),
      valor: tipoCampoId === TIPO_CAMPO_BOOLEANO ? normalizeBooleano(valor) : normalizeValor(valor),
      tipo_campo_id: tipoCampoId,
      campo_chave: personalizado ? personalizado.campo_chave : (condicao.campo_chave || condicao.campo?.chave),
      ...(personalizado ? { campo_personalizado_id: personalizado.campo_personalizado_id } : {})
    };
  }).filter((condicao) => !!condicao.campo_id && !!condicao.operador_id);

  // Independente do que vier da API, condições do mesmo grupo são sempre exibidas como E/AND.
  return normalizadas.map((condicao, index) => ({
    ...condicao,
    operador_logico_id: (index === 0 ? undefined : DEFAULT_OPERADOR_LOGICO_AND_ID) as OperadorLogicoFiltroAvancadoV2
  } as CondicaoFiltroAvancadoV2));
}

export function normalizeGrupos(grupos: any[]): GrupoCondicoesFiltroAvancadoV2[] {
  return (grupos || []).map((grupo: any) => ({
    operador_logico_id: DEFAULT_OPERADOR_LOGICO_OR_ID as OperadorLogicoFiltroAvancadoV2,
    condicoes: normalizeCondicoes(grupo.condicoes || grupo.filterCondicion || [])
  }));
}

export function gruposFromApi(grupos: any[], campos: any[] = []): GrupoCondicoesFiltroAvancadoV2[] {
  return (grupos || []).map((grupo: any) => ({
    operador_logico_id: DEFAULT_OPERADOR_LOGICO_OR_ID as OperadorLogicoFiltroAvancadoV2,
    condicoes: condicoesFromApi(grupo.condicoes || grupo.filterCondicion || [], campos)
  }));
}

// Condição gravada no campo contêiner volta para o campo personalizado da tela (nome e tipo vêm da lista de campos;
// campo que não está mais ativo continua com o nome do contêiner e o id do campo).
function campoPersonalizadoFromApi(condicao: any, campos: any[]): { campo_personalizado_id: number; valor: any; campo_text: string; tipo_campo_id: number; campo_chave: string } | null {
  if ((condicao.campo_chave || condicao.campo?.chave) !== CHAVE_CAMPOS_PERSONALIZADOS) {
    return null;
  }

  let parsed: any = condicao.valor;
  try {
    parsed = typeof condicao.valor === 'string' ? JSON.parse(condicao.valor) : condicao.valor;
  } catch {
    return null;
  }
  if (!parsed || parsed.campo_personalizado_id === undefined || parsed.campo_personalizado_id === null) {
    return null;
  }

  const campoPersonalizadoId = Number(parsed.campo_personalizado_id);
  const campo = (campos || []).find((item) => Number(item.campo_personalizado_id) === campoPersonalizadoId);
  return {
    campo_personalizado_id: campoPersonalizadoId,
    valor: parsed.valor,
    campo_text: campo ? campo.text : `${condicao.campo_text || condicao.campo?.text} #${campoPersonalizadoId}`,
    tipo_campo_id: campo ? Number(campo.tipo_campo_id) : normalizeId(condicao.tipo_campo_id || condicao.campo?.tipo_campo_id),
    campo_chave: PREFIXO_CHAVE_CAMPO_PERSONALIZADO + campoPersonalizadoId
  };
}

export function normalizePeriodos(periodos: any[]): PeriodoFiltroAvancadoV2[] {
  const normalizados = (periodos || []).map((periodo: any) => {
    const operadorId = normalizeId(periodo.operador_id || periodo.operador?.id);
    return {
      campo_id: normalizeId(periodo.campo_id || periodo.campo?.id),
      campo_text: periodo.campo_text || periodo.campo?.text,
      operador_id: operadorId,
      valor: normalizePeriodoValor(periodo, operadorId),
      dinamico: isPeriodoDinamico(periodo),
      entidade_id: normalizeId(periodo.entidade_id || periodo.entidade?.id || periodo.campo?.entidade_id),
      tipo_campo_id: normalizeId(periodo.tipo_campo_id || periodo.campo?.tipo_campo_id),
      campo_chave: periodo.campo_chave || periodo.campo?.chave,
      operador_logico_id: normalizeId(periodo.operador_logico_id || periodo.operadorLogicoId)
    };
  }).filter((periodo) => !!periodo.campo_id && !!periodo.operador_id);

  return normalizados.map(({ operador_logico_id, ...periodo }, index) => {
    if (index === normalizados.length - 1) {
      return periodo as PeriodoFiltroAvancadoV2;
    }

    const proximoOperador = normalizados[index + 1].operador_logico_id;
    return {
      ...periodo,
      operador_logico_id: (proximoOperador || DEFAULT_OPERADOR_LOGICO_AND_ID) as OperadorLogicoFiltroAvancadoV2
    } as PeriodoFiltroAvancadoV2;
  });
}

export function periodosFromApi(periodos: any[]): PeriodoFiltroAvancadoV2[] {
  const condicoes = (periodos || []).reduce((acc: any[], grupo: any) => {
    return acc.concat(Array.isArray(grupo?.condicoes) ? grupo.condicoes : [grupo]);
  }, [] as any[]);

  return condicoes.map((periodo: any, index: number) => {
    const operadorId = normalizeId(periodo.operador_id || periodo.operador?.id);
    const operadorAnterior = index === 0 ? null : normalizeId(condicoes[index - 1].operador_logico_id || condicoes[index - 1].operadorLogicoId);

    return {
      campo_id: normalizeId(periodo.campo_id || periodo.campo?.id),
      campo_text: periodo.campo_text || periodo.campo?.text,
      operador_id: operadorId,
      valor: normalizePeriodoValor(periodo, operadorId),
      dinamico: isPeriodoDinamico(periodo),
      entidade_id: normalizeId(periodo.entidade_id || periodo.entidade?.id || periodo.campo?.entidade_id),
      tipo_campo_id: normalizeId(periodo.tipo_campo_id || periodo.campo?.tipo_campo_id),
      campo_chave: periodo.campo_chave || periodo.campo?.chave,
      operador_logico_id: (operadorAnterior || undefined) as OperadorLogicoFiltroAvancadoV2
    } as PeriodoFiltroAvancadoV2;
  }).filter((periodo) => !!periodo.campo_id && !!periodo.operador_id);
}

// O manager não devolve a marca "dinâmico": a data dinâmica é reconhecida pela chave ($today, $last_seven_days...).
function isPeriodoDinamico(periodo: any): boolean {
  return periodo.dinamico === true || periodo.data_dinamica === true || (typeof periodo.valor === 'string' && periodo.valor.startsWith('$'));
}

function normalizePeriodoValor(periodo: any, operadorId: number | null): any {
  // valor_inicio/valor_fim só ficam preenchidos quando o operador do período é "Entre".
  if (periodo.valor_inicio || periodo.valor_fim) {
    return [periodo.valor_inicio, periodo.valor_fim];
  }

  return normalizeValor(periodo.valor);
}

// Sim/Não: o manager devolve o valor gravado como texto ('1'/'0'; filtros antigos 'true'/'false').
const TIPO_CAMPO_BOOLEANO = 5;

function normalizeBooleano(valor: any): boolean | null {
  if (valor === null || valor === undefined || valor === '') {
    return null;
  }

  return ['1', 'true'].includes(String(Array.isArray(valor) ? valor[0] : valor).toLowerCase());
}

function normalizeValor(valor: any): any {
  if (Array.isArray(valor)) {
    return valor.map((item) => normalizeValor(item));
  }

  if (valor && typeof valor === 'object') {
    if (valor.id !== undefined && Object.keys(valor).length <= 2) {
      return valor.id;
    }

    return valor;
  }

  return valor;
}

// Modo TV: filtro_personalizado_id guarda o id do filtro novo (número) ou o ObjectId do filtro antigo (V1).
export function idFiltroNovoModoTv(id: any): number | null {
  return /^\d+$/.test(String(id ?? '')) ? Number(id) : null;
}

// Há filtro avançado novo salvo para a tela: a 1ª carga vem dele (o componente o reaplica ao abrir);
// a carga sem o filtro poderia chegar depois e sobrescrever a tela filtrada.
// As dashboards de ticket compartilham o filtro selecionado (trocar de dashboard mantém o filtro). O Modo TV
// (id na rota ou ?modo-tv) fica de fora: ele aplica o filtro do grupo.
export function chaveFiltroSalvo(tela: string): string {
  return /^\/dashboards\/[a-z-]+$/.test(tela || '') ? 'filtros_/dashboards' : 'filtros_' + tela;
}

export function possuiFiltroNovoSalvo(storageService: { getStoredItemSimple(chave: string): any }, tela: string): boolean {
  const salvo = storageService.getStoredItemSimple(chaveFiltroSalvo(tela));
  return !!(salvo && (salvo.filter_id?.length || salvo.search));
}

function normalizeId(value: any): number | null {
  if (value && typeof value === 'object' && value.id !== undefined) {
    return Number(value.id);
  }

  return value !== undefined && value !== null && value !== '' ? Number(value) : null;
}

function hasItems(value: any): boolean {
  return Array.isArray(value) ? value.length > 0 : !!value;
}

function hasProperty(source: any, keys: string[]): boolean {
  return keys.some((key) => Object.prototype.hasOwnProperty.call(source, key));
}
